import { createRouter, createWebHistory } from 'vue-router';

import { store } from '@/store';
import { setItemInLocalStorageWithExpiry, showToastError, showToastSuccess } from '@/helpers';
import {
  findPermission,
  getIsAuthenticated,
  HasNoPermissions,
  Logout, Me, resetPermissions, setPermissionsString,
  hasAccessToFeatures, SwitchActiveShop,
  UserState,
} from '@/composables/User';
import MainLayout from '@/views/layout/LayoutMain.vue';
import LayoutAuthentication from '@/views/layout/LayoutAuthentication.vue';
import ChooseShop from '@/views/auth/ChooseShop.vue';
import Dashboard from '@/views/Dashboard.vue';
import MyTemplates from '@/views/MyTemplates.vue';
import Templates from '@/views/Templates.vue';
import Login from '@/views/auth/Login.vue';
import AutomatedScenarioList from '@/views/automated-scenarios/AutomatedScenarioList.vue';
import ShopEdit from '@/views/shop/ShopEdit.vue';
import MyNotifications from '@/views/shop/MyNotifications.vue';
import MyDomains from '@/views/shop/MyDomains.vue';
import MyDomainsV2 from '@/views/shop/MyDomainsV2.vue';
import BillingShopifyReturn from '@/views/billing/BillingShopifyReturn.vue';
import SubscriptionPage from '@/views/billing/SubscriptionPage.vue';
import LayoutSubscription from '@/views/layout/LayoutSubscription.vue';
import GdprLogs from '@/views/shop/GdprLogs.vue';
import ListsManageV2 from '@/views/mes-listes/ListsManageV2.vue';
import Profile from '@/views/user/Profile.vue';
import MyShopSyncs from '@/views/my-shop-data/MyShopSyncs.vue';
import SyncList from '@/views/my-shop-data/SyncList.vue';
import ObjectsDataList from '@/views/my-shop-data/ObjectsDataList.vue';
import SystemObjectsList from '@/views/my-shop-data/SystemObjectsList.vue';
import Carriers from '@/views/my-shop-data/Carriers.vue';
import CarriersV2 from '@/views/my-shop-data/CarriersV2.vue';
import Customers from '@/views/my-shop-data/Customers.vue';
import CustomersV2 from '@/views/my-shop-data/CustomersV2.vue';
import CustomerGroups from '@/views/my-shop-data/CustomerGroups.vue';
import CustomerGroupsV2 from '@/views/my-shop-data/CustomerGroupsV2.vue';
import Manufacturers from '@/views/my-shop-data/Manufacturers.vue';
import ManufacturersV2 from '@/views/my-shop-data/ManufacturersV2.vue';
import Orders from '@/views/my-shop-data/Orders.vue';
import OrdersV2 from '@/views/my-shop-data/OrdersV2.vue';
import OrderStatus from '@/views/my-shop-data/OrderStatus.vue';
import OrderStatusV2 from '@/views/my-shop-data/OrderStatusV2.vue';
import ProductCategories from '@/views/my-shop-data/ProductCategories.vue';
import ProductCategoriesV2 from '@/views/my-shop-data/ProductCategoriesV2.vue';
import Products from '@/views/my-shop-data/Products.vue';
import ProductsV2 from '@/views/my-shop-data/ProductsV2.vue';
import Vouchers from '@/views/my-shop-data/Vouchers.vue';
import VouchersV2 from '@/views/my-shop-data/VouchersV2.vue';
import Events from '@/views/my-shop-data/Events.vue';
import Webhooks from '@/views/my-shop-data/Webhooks.vue';
import CustomObjects from '@/views/my-shop-data/CustomObjects.vue';
import ManageMyAccount from '@/views/user/ManageMyAccount.vue';
import ListsContacts from '@/views/mes-listes/customers/ListsContacts.vue';
import Contacts from '@/views/mes-listes/customers/Contacts.vue';
import ImportReportContacts from '@/views/mes-listes/customers/ImportReportContacts.vue';
import ListsRejects from '@/views/mes-listes/ListsRejects.vue';
import ListsConsents from '@/views/mes-listes/ListsConsents.vue';
import MarketingPressure from '@/views/marketing-pressure/MarketingPressure.vue';
import AutomatedScenarioMain from '@/views/automated-scenarios/AutomatedScenarioMain.vue';
import NotFoundComponent404 from '@/views/errors/NotFoundComponent404.vue';
import ForgotPassword from '@/views/auth/ForgotPassword.vue';
import SpeedRegistration from '@/views/auth/SpeedRegistration.vue';
import BulkCampaignsList from '@/views/automated-scenarios/BulkCampaignsList.vue';
import CampaignsBulkList from '@/views/workflows/CampaignsBulkList.vue';
import CampaignsAutomationList from '@/views/workflows/CampaignsAutomationList.vue';
import WorkflowViewer from '@/views/workflows/WorkflowViewer.vue';
import PermissionsByShop from '@/views/user/PermissionsByShop.vue';
import SubUserList from '@/views/user/SubUserList.vue';
import StatsCustoms from '@/views/stats/StatsCustoms.vue';
import StatsList from '@/views/user/StatsList.vue';
import StatsListV2 from '@/views/user/StatsListV2.vue';
import { i18n } from '@/i18n';
import MyOldNewslettersStatistics from '@/views/shop/MyOldNewslettersStatistics.vue';
import CompleteV2 from '@/views/CompleteV2.vue';
import MigrationV5 from '@/views/MigrationV5.vue';
import AccountActivation from '@/views/auth/AccountActivation.vue';
import ShopifySso from '@/views/auth/ShopifySso.vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { redirectIfV4Shop, isShopCurrentVersionV5, getV4DashboardUrl } from '@/composables/shopVersion';
import { checkIfDbCreated, checkPlatformMigrationV5 } from '@/configs/complete';
// eslint-disable-next-line import/no-cycle
import { fetchMigrationV5Status, isServedByV5 } from '@/composables/migrationV5';
import axios from 'axios';
import AdvisorNewLink from '@/views/auth/AdvisorNewLink.vue';
import UserExports from '@/views/user/UserExports.vue';
import ChangePassword from '@/views/auth/ChangePassword.vue';
import DownloadFile from '@/views/shop/DownloadFile.vue';
import ShopApiAccess from '@/views/shop/ShopApiAccess.vue';
import ExportDownload from '@/views/storage/download/ExportDownload.vue';
import ExportsList from '@/views/user/ExportsList.vue';
import ShopIntegrations from '@/views/shop/ShopIntegrations.vue';
import IntegrationConfig from '@/views/shop/IntegrationConfig.vue';
import IntegrationsComingSoon from '@/views/shop/IntegrationsComingSoon.vue';

/**
 * Point de vérité UNIQUE du gating de la marketplace ShopiMind.
 *
 * La marketplace est ACTIVE : les routes `shop.Integrations` /
 * `shop.IntegrationConfig` résolvent sur `ShopIntegrations` / `IntegrationConfig`
 * et ce drapeau vaut `true`. Tous les points d'entrée UI qui mènent vers la
 * marketplace (CTA « installer » du `RequireIntegrationDialog`, liens
 * marketplace du panneau de widgets, modale d'exploration des widgets) le
 * consultent : quand il vaut `true`, ils naviguent vers la marketplace.
 *
 * Masquer la marketplace = repasser ce drapeau à `false` ET rebrancher les
 * routes sur `IntegrationsComingSoon` — les deux au même endroit ; les points
 * d'entrée UI afficheront alors « Bientôt disponible » et désactiveront la
 * navigation.
 */
export const MARKETPLACE_ENABLED = true;


type Redirection = boolean | { name: string };
let translation: any;
(async () => {
  translation = await i18n;
})();

async function userGuard(onOk: Redirection, onFailure: Redirection) {
  let isAuthenticated = getIsAuthenticated();
  if (isAuthenticated) {
    return onOk;
  }
  store.commit('general/showTheSpinner');
  try {
    await Me(true);
    isAuthenticated = getIsAuthenticated();
    if (!isAuthenticated) {
      return onFailure;
    }
    // Filet de sécurité: si /auth/me renvoie un shop dont la version installée est < 5.0.0,
    // on force le logout et on renvoie l'utilisateur sur le dashboard v4.
    //
    // EXCEPTION: un shop SERVI PAR V5 (migration en cours OU finalisée) a
    // légitimement `currentVersion='4.x.x'` dans le store (valeur souvent
    // périmée) jusqu'à ce que le user réinstalle son module V5. On tranche donc
    // sur le STATUT migration (source de vérité backend), pas sur la version du
    // store : `isServedByV5` couvre in_progress → finalized. Sans ça, le filet
    // déconnecte le shop (ex. connexion module = statut '2', ou post-finalize)
    // et le renvoie sur V4 → re-redirection sur /migration-v5/ → boucle infinie
    // avec logout à chaque tour.
    const activeShopVersion = UserState.activeShop?.currentVersion;
    if (UserState.activeShop && !isShopCurrentVersionV5(activeShopVersion)) {
      const migrationStatus = await fetchMigrationV5Status();
      if (!isServedByV5(migrationStatus)) {
        await Logout();
        window.location.href = getV4DashboardUrl();
        return onFailure;
      }
    }
    if (HasNoPermissions()) {
      await Logout();
      resetPermissions();
      await showToastError(translation.global.t('user.subUser.loginError'));
      return onFailure;
    }
    setPermissionsString();
    return onOk;
  } catch (error: any) {
    return onFailure;
  } finally {
    store.commit('general/hideTheSpinner');
  }
}

async function checkAccessAndRedirect(to: any, from: any, featureKey: string, permissionKey: string, redirectRoute = 'home') {
  if (!findPermission(permissionKey)) {
    return { name: redirectRoute };
  }

  const featurePermission = hasAccessToFeatures(featureKey);
  if (featurePermission && !featurePermission.access) {
    store.commit('general/setIsFeatureUnavailableModalVisible', true);
    store.commit('general/setLastRoute', { to, from });
    store.commit('general/setFeatureUnavailableMinPlan', featurePermission.minimumPlan);
    return false;
  }

  return true;
}

const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: 'dashboard',
        name: 'home',
        alias: '/',
        component: Dashboard,
      },
      {
        path: '/404',
        name: 'not-found',
        component: NotFoundComponent404,
      },
      {
        path: 'my-templates',
        name: 'template-manage',
        component: MyTemplates,
        beforeEnter: async () => {
          if (!findPermission('my_templates.view')) {
            return ({ name: '/' });
          }
          return true;
        },
      },
      {
        path: 'templates',
        name: 'template-manage-v2',
        component: Templates,
        beforeEnter: async () => {
          if (!findPermission('my_templates.view')) {
            return ({ name: '/' });
          }
          return true;
        },
      },
      {
        path: '/',
        component: AutomatedScenarioMain,
        children: [
          {
            path: 'automated-scenarios',
            name: 'workflow',
            component: AutomatedScenarioList,
            beforeEnter: async () => {
              if (!findPermission('my_templates.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'workflows/:type/:id/view',
            name: 'workflow-viewer',
            component: WorkflowViewer,
            beforeEnter: async () => {
              if (!findPermission('my_templates.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'marketing-pressure',
            name: 'marketing-pressure',
            component: MarketingPressure,
            beforeEnter: async () => {
              if (!findPermission('automated_scenarios.marketing_pressure.view')) {
                return ({ name: '/' });
              }
              return true;
            },
          },
        ],
      },
      {
        path: 'campaigns-bulk',
        name: 'campaigns-bulk',
        component: BulkCampaignsList,
        beforeEnter: async () => {
          if (!findPermission('bulk_campaigns.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'campaigns-bulk-list',
        name: 'campaigns-bulk-list',
        component: CampaignsBulkList,
        beforeEnter: async () => {
          if (!findPermission('bulk_campaigns.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'campaigns-automation-list',
        name: 'campaigns-automation-list',
        component: CampaignsAutomationList,
        beforeEnter: async () => {
          if (!findPermission('automated_scenarios.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
    ],
    beforeEnter: async (to: any, from: any, next: Function) => {
      const result = await userGuard(true, false);
      if (result) {
        // The V4 → V5 platform migration check runs FIRST: a shop in
        // `pending` or `ready` is by definition past the original
        // onboarding (it has been provisioned in V4), so we don't want
        // to bounce it to `/complete/`. The migration page takes
        // priority. Both checks run in parallel — they're independent
        // and we save a round-trip.
        const [migrationOk, dbCreated] = await Promise.all([
          checkPlatformMigrationV5(),
          checkIfDbCreated(),
        ]);
        if (!migrationOk) {
          next('/migration-v5');
          return;
        }
        if (!dbCreated) {
          next('/complete');
          return;
        }
        next();
      } else {
        // si on navigue sur http://domain.com directement ou a travers le "button" back on, on redirige vers http://domain.com/auth/login
        // eslint-disable-next-line no-lonely-if
        if (from.fullPath === '/') {
          next({ name: 'login' });
        }
      }
    },
  },

  {
    path: '/complete/',
    component: CompleteV2,
    beforeEnter: async (to: any, from: any, next: Function) => {
      const result = await userGuard(true, false);
      if (result) {
        // Migration takes priority over onboarding (a shop in active
        // migration is already past onboarding by construction).
        const migrationOk = await checkPlatformMigrationV5();
        if (!migrationOk) {
          next('/migration-v5');
          return;
        }
        const dbCreated = await checkIfDbCreated();
        if (dbCreated) {
          next('/');
        } else {
          next();
        }
      } else {
        next({ name: 'login' });
      }
    },
  },

  {
    path: '/migration-v5/',
    component: MigrationV5,
    beforeEnter: async (to: any, from: any, next: Function) => {
      const result = await userGuard(true, false);
      if (result) {
        const migrationOk = await checkPlatformMigrationV5();
        // `migrationOk === true` ⇒ no migration in progress ⇒ the user
        // shouldn't be on this page. Bounce them to home (router's main
        // guard will then route them to /complete/ if onboarding is
        // incomplete). Otherwise let them through.
        if (migrationOk) {
          next('/');
          return;
        }
        next();
      } else {
        next({ name: 'login' });
      }
    },
  },

  {
    path: '/shop/',
    component: MainLayout,
    children: [
      {
        path: 'edit',
        name: 'shop.ShopEdit',
        component: ShopEdit,
        beforeEnter: async () => {
          if (!findPermission('store_params.edit')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'api-access',
        name: 'shop.ApiAccess',
        component: ShopApiAccess,
        beforeEnter: async () => {
          if (!findPermission('api_access.edit')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        // Page « Bientôt disponible » : la marketplace est désactivée le temps des
        // développements. Aucun appel backend n'est effectué.
        path: 'integrations',
        name: 'shop.Integrations',
        component: MARKETPLACE_ENABLED ? ShopIntegrations : IntegrationsComingSoon,
      },
      {
        // Route de configuration redirigée elle aussi vers la page « Bientôt
        // disponible » tant que la marketplace est désactivée.
        path: 'integrations/:slug/configure',
        name: 'shop.IntegrationConfig',
        component: MARKETPLACE_ENABLED ? IntegrationConfig : IntegrationsComingSoon,
      },
      {
        path: 'send-domains-old/:token?',
        name: 'shop.MyDomains.Old',
        component: MyDomains,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('my_domains.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'send-domains/:token?',
        name: 'shop.MyDomains',
        component: MyDomainsV2,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('my_domains.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      // Legacy entry point — always redirects to /shop-subscription
      // (the single subscription management page for V3/V4/V5 shops).
      // The route name `shop.MyOffer` is kept so existing
      // router.push({ name: 'shop.MyOffer' }) callers keep working.
      {
        path: 'plan',
        name: 'shop.MyOffer',
        redirect: { name: 'shop-subscription' },
      },
      // Legacy `/shop/bills` (Symfony ShopBills) is gone — invoices live in
      // the new SubscriptionPage now. We keep the route as a redirect so
      // bookmarks, V4-dashboard sidebar links and external emails pointing
      // here all land on the unified billing page.
      {
        path: 'bills',
        name: 'shop.LegacyBills',
        redirect: { name: 'shop-subscription' },
      },
      {
        path: 'billing/return-from-shopify',
        name: 'shop.BillingShopifyReturn',
        component: BillingShopifyReturn,
        beforeEnter: async () => {
          if (!findPermission('my_offer.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'old-newsletters-statistics',
        name: 'shop.MyOldNewslettersStatistics',
        component: MyOldNewslettersStatistics,
        beforeEnter: async () => true,
      },
      {
        path: 'notifications',
        name: 'shop.MyNotifications',
        component: MyNotifications,
        beforeEnter: async () => {
          if (!findPermission('notifications.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'download-file/:encodedString',
        name: 'shop.DownloadFile',
        component: DownloadFile,
        props: true,
      },
      {
        path: 'logs',
        name: 'shop.gdpr-logs',
        component: GdprLogs,
        beforeEnter: async () => {
          if (!findPermission('my_bills.gdpr.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'data/',
        name: 'shop.data',
        component: SyncList,
        beforeEnter: async () => {
          if (!findPermission('my_data.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'objects-data-list',
        name: 'shop.objects-data-list',
        component: ObjectsDataList,
        beforeEnter: async () => {
          if (!findPermission('my_data.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'system-objects',
        name: 'shop.system-objects',
        component: SystemObjectsList,
        beforeEnter: async () => {
          if (!findPermission('my_data.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'events/',
        name: 'shop.events',
        component: Events,
        beforeEnter: async () => {
          if (!findPermission('my_events.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'webhooks/',
        name: 'shop.webhooks',
        component: Webhooks,
        beforeEnter: async () => {
          if (!findPermission('my_webhooks.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'syncs/',
        name: 'shop.syncs',
        component: MyShopSyncs,
        children: [
          {
            path: 'carriers',
            name: 'shop.syncs.carriers',
            component: CarriersV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'customers-groups',
            name: 'shop.syncs.customers_groups',
            // component: CustomerGroups,
            component: CustomerGroupsV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'customers',
            name: 'shop.syncs.customers',
            // component: Customers,
            component: CustomersV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'manufacturers',
            name: 'shop.syncs.manufacturers',
            // component: Manufacturers,
            component: ManufacturersV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'orders',
            name: 'shop.syncs.orders',
            // component: Orders,
            component: OrdersV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'orderStatus',
            name: 'shop.syncs.orders_status',
            // component: OrderStatus,
            component: OrderStatusV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'products',
            name: 'shop.syncs.products',
            // component: Products,
            component: ProductsV2,
            // beforeEnter: async () => {
            //   if (!findPermission('my_data.view')) {
            //     return ({ name: 'home' });
            //   }
            //   return true;
            // },
          },
          {
            path: 'product-categories',
            name: 'shop.syncs.products_categories',
            // component: ProductCategories,
            component: ProductCategoriesV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'vouchers',
            name: 'shop.syncs.vouchers',
            // component: Vouchers,
            component: VouchersV2,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
          {
            path: 'custom-objects',
            name: 'shop.syncs.custom-objects',
            component: CustomObjects,
            beforeEnter: async () => {
              if (!findPermission('my_data.view')) {
                return ({ name: 'home' });
              }
              return true;
            },
          },
        ],
      },
    ],
    beforeEnter: async () => userGuard(true, { name: 'login' }),
  },

  {
    // Standalone subscription management page. Top-level route with its own
    // minimal layout (logo + back) — does NOT share LayoutMain with the rest
    // of the shop dashboard.
    path: '/shop-subscription',
    component: LayoutSubscription,
    beforeEnter: async () => userGuard(true, { name: 'login' }),
    children: [
      {
        path: '',
        name: 'shop-subscription',
        component: SubscriptionPage,
        beforeEnter: async () => {
          if (!findPermission('my_offer.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
    ],
  },

  {
    path: '/user/',
    component: MainLayout,
    children: [
      // Legacy "Mon offre" tab — redirected to the standalone subscription
      // page. Must come BEFORE the parameterised `profile/:tab?` route so
      // Vue Router matches this specific path first.
      {
        path: 'profile/offer',
        redirect: { name: 'shop-subscription' },
      },
      {
        path: 'profile/:tab?',
        name: 'user.profile',
        component: Profile,
        props: true,
      },
      // {
      //   path: 'manage-account',
      //   name: 'user.manage-my-account',
      //   component: ManageMyAccount,
      // },
      {
        path: 'manage-sub-users/:userId?',
        name: 'user.manage-sub-users',
        component: PermissionsByShop,
        props: true,
        beforeEnter: async (to: any, from: any) => checkAccessAndRedirect(to, from, 'user.manage-sub-users', 'admin'),
      },
      {
        path: 'sub-user-list',
        name: 'user.sub-user-list',
        component: SubUserList,
        beforeEnter: async () => {
          if (!findPermission('admin')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'stats-list',
        name: 'users.stats',
        component: StatsList,
        beforeEnter: async (to: any, from: any) => checkAccessAndRedirect(to, from, 'users.stats', 'stats_report.view'),
      },
      {
        path: 'stats-list-v2',
        name: 'users.stats.v2',
        component: StatsListV2,
        beforeEnter: async (to: any, from: any) => checkAccessAndRedirect(to, from, 'users.stats.v2', 'stats_report.view'),
      },
      {
        path: 'exports-list',
        name: 'users.exports',
        component: ExportsList,
      },
    ],
    beforeEnter: async () => userGuard(true, { name: 'login' }),
  },

  {
    path: '/lists/',
    component: MainLayout,
    children: [
      {
        path: 'manage',
        name: 'lists-manage',
        component: ListsManageV2,
        beforeEnter: async (to: any, from: any, next: Function) => {
          if (!findPermission('lists-segments.manage_lists')) {
            next({ name: 'home' });
          } else {
            next();
          }
        },
      },
      {
        path: 'manage-contacts/:id',
        name: 'lists.manage-contacts',
        component: ListsContacts,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('lists-segments.modify_clients')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'contacts',
        name: 'lists.contacts',
        component: Contacts,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('lists-segments.modify_clients')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'consents',
        name: 'lists.consents',
        component: ListsConsents,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('consents.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'rejects',
        name: 'lists.rejects',
        component: ListsRejects,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('rejets.view')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'import-report/contacts/:id',
        name: 'lists.import-report-contacts',
        component: ImportReportContacts,
        props: true,
        beforeEnter: async () => {
          if (!findPermission('lists-segments.modify_clients')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
    ],
    beforeEnter: async () => userGuard(true, { name: 'login' }),
  },

  {
    path: '/custom_stats/',
    component: MainLayout,
    children: [
      {
        path: 'custom_stats_:pageId([0-9]+)',
        name: 'custom_stats',
        props: (route: any) => ({ pageId: parseInt(route.params.pageId, 10) }),
        component: StatsCustoms,
      },
    ],
    beforeEnter: async () => userGuard(true, { name: 'login' }),
  },

  {
    path: '/custom-kpis/',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'custom-kpis.list',
        component: () => import('@/views/custom-stats/CustomKpiList.vue'),
        beforeEnter: async () => {
          if (!findPermission('custom_stats.read')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: 'new',
        name: 'custom-kpis.create',
        component: () => import('@/views/custom-stats/CustomKpiEditor.vue'),
        beforeEnter: async () => {
          if (!findPermission('custom_stats.create')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
      {
        path: ':id([0-9]+)',
        name: 'custom-kpis.edit',
        component: () => import('@/views/custom-stats/CustomKpiEditor.vue'),
        beforeEnter: async () => {
          if (!findPermission('custom_stats.update')) {
            return ({ name: 'home' });
          }
          return true;
        },
      },
    ],
    beforeEnter: async () => userGuard(true, { name: 'login' }),
  },

  {
    path: '/auth',
    component: LayoutAuthentication,
    children: [
      {
        path: 'login',
        name: 'login',
        component: Login,
        beforeEnter: async () => userGuard({ name: 'home' }, true),
      },
      {
        path: 'forgot-password',
        name: 'user.forgot-password',
        component: ForgotPassword,
        // beforeEnter: async () => userGuard({ name: 'dashboard' }, true),
      },
      {
        path: 'change-password/:token',
        name: 'user.change-password',
        component: ChangePassword,
        props: true,
        beforeEnter: async (to: any, from: any, next: any) => {
          const { token } = to.params;
          try {
            const validity = await nestCall('get', NestApiType.AUTH, `/auth/reset-token/${token}`, {}, '', {}, false);

            if (!validity || !validity.success) {
              await showToastError('Le lien pour réinitialiser le mot de passe est invalide ou a expiré.');
              next({ name: 'login' });
            }
          } catch (e) {
            await showToastError('GENERIC_ERROR');
          }
          next();
        },
      },
      {
        path: 'registration/speed',
        name: 'user.registration-speed',
        component: SpeedRegistration,
        beforeEnter: async (to: any, from: any, next: any) => {
          const statusRegistration = await nestCall('get', NestApiType.AUTH, '/auth/register-state', {}, '', {}, false);
          if (statusRegistration && statusRegistration.success && statusRegistration.status === 'closed') {
            await showToastError('Les inscriptions sont temporairement suspendues. Veuillez réessayer ultérieurement.');
            next({ name: 'login' });
          } else {
            next();
          }
        },
      },
      {
        // Login from the Shopify app (signed assertion of the connector, read at startup by shopifySsoAssertion.ts).
        path: '/shopify/sso',
        name: 'shopify.sso',
        component: ShopifySso,
      },
      {
        path: '/account/activation/:idUser/:token',
        name: 'user.activation',
        component: AccountActivation,
        beforeEnter: async (to: any, from: any, next: any) => {
          // Accédez aux paramètres de la route en tant que props
          const { idUser, token } = to.params;
          try {
            const resultActivation = await nestCall('get', NestApiType.AUTH, `/auth/activate/${idUser}/${token}`, {}, '', {}, false);

            if (resultActivation && resultActivation.success) {
              await showToastSuccess('Compte activé avec succès!');
            } else if (resultActivation.reason) {
              await showToastError(resultActivation.reason);
            } else {
              await showToastError('GENERIC_ERROR');
            }
          } catch (e) {
            await showToastError('GENERIC_ERROR');
          }

          next({ name: 'login' });
        },
      },
      {
        path: '/send-domains/activation/:idShop/:token',
        name: 'send-domains.activation',
        redirect: (to: any) => ({
          name: 'shop.MyDomains',
          params: { token: to.params.token },
        }),
      },

    ],
  },

  {
    path: '/shopify',
    component: AccountActivation,
    children: [
      {
        path: 'auth/:dataBase64/:hmac',
        name: 'shopify.auto.auth',
        component: AccountActivation,
        beforeEnter: async (to: any, from: any, next: any) => {
          store.commit('general/showTheSpinner');
          await Logout();
          const { dataBase64, hmac } = to.params;
          try {
            if (dataBase64 && hmac) {
              const res: any = await nestCall(
                'post',
                NestApiType.AUTH,
                '/auth/alt-login',
                {},
                '?want_cookie=true',
                { data: dataBase64, token: hmac },
                false,
              );
              if (res && res.success) {
                const { idShop } = res;
                await Me(true, Number(idShop));
                await showToastSuccess(translation.global.t('login_success'));
                next({ name: 'home' });
              } else {
                await showToastError('GENERIC_ERROR');
              }
            }
          } catch (e) {
            await showToastError('GENERIC_ERROR');
          }
          store.commit('general/hideTheSpinner');

          next({ name: 'login' });
        },
      },
    ],
  },

  {
    path: '/choose-shop',
    name: 'choose-shop',
    component: ChooseShop,
  },

  {
    path: '/storage/download/:downloadType/:idExport',
    name: 'storage.download',
    component: ExportDownload,
    beforeEnter: async (to: any, from: any, next: Function) => {
      const result = await userGuard(true, false);
      if (result) {
        next();
      } else {
        next({ name: 'login' });
      }
    },
  },

  {
    path: '/advisor/new-link/:token',
    name: 'advisor.newlink',
    component: AdvisorNewLink,
    beforeEnter: async (to: any, from: any, next: any) => {
      // Accédez aux paramètres de la route en tant que props
      const { token } = to.params;
      try {
        const { data }: Record<string, any> = await axios.create({ baseURL: '/api' }).get(`/advisor/new-link?token=${token}`);

        if (data && data.success) {
          await showToastSuccess('success');
        } else if (data.error) {
          await showToastError(data.error);
        } else {
          await showToastError('GENERIC_ERROR');
        }
      } catch (e) {
        await showToastError('GENERIC_ERROR');
      }

      next({ name: 'login' });
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { pi, pireferer } = to.query;
  // Si un code promo existe dans l'URL, on le sauvegarde dans le localStorage
  if (pi) {
    setItemInLocalStorageWithExpiry('promoCodePartner', pi as string, 2);
  }
  if (pireferer) {
    window.location.href = pireferer as string;
    return;
  }

  if (await redirectIfV4Shop(to.path)) {
    return;
  }

  if (to.query.cp) {
    const query = { ...to.query };
    delete query.cp;
    next({ path: to.path, query, replace: true });
  } else {
    next();
  }
});

export default router;
